import React, { useEffect, useState, useCallback } from 'react';
import { Table, Select, Button, Space, Tag } from 'antd';
import { CSVLink } from 'react-csv';
import { FileExcelOutlined, DollarOutlined } from '@ant-design/icons';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import { useTranslation } from 'react-i18next';

const { Option } = Select;

const generateMonthYearOptions = () => {
  const options = [];
  const startYear = 2025;
  const startMonth = 1;
  const currentYear = new Date().getFullYear();

  for (let year = startYear; year <= currentYear + 5; year++) {
    for (let month = 0; month < 12; month++) {
      if (year === startYear && month < startMonth) continue;

      const monthYear = new Date(year, month).toLocaleString('default', {
        month: 'short',
        year: 'numeric',
      }).toUpperCase();

      options.push({ value: `${year}-${month + 1}`, label: monthYear });
    }
  }

  return options;
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = date.toLocaleString('default', { month: 'short' });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

const calculateEndTime = (startTime, duration) => {
  if (!startTime || !duration) return 'N/A';
  
  // Parse start time
  const [startHours, startMinutes] = startTime.split(':').map(Number);
  
  // Parse duration based on format like "2h 30m"
  let durationHours = 0;
  let durationMinutes = 0;
  
  // Check if duration is in "Xh Ym" format
  if (duration.includes('h') || duration.includes('m')) {
    const hoursMatch = duration.match(/(\d+)h/);
    const minutesMatch = duration.match(/(\d+)m/);
    
    durationHours = hoursMatch ? parseInt(hoursMatch[1]) : 0;
    durationMinutes = minutesMatch ? parseInt(minutesMatch[1]) : 0;
  } 
  // If it's in "HH:MM" format
  else if (duration.includes(':')) {
    [durationHours, durationMinutes] = duration.split(':').map(Number);
  }
  
  // Calculate end time
  let endHours = startHours + durationHours;
  let endMinutes = startMinutes + durationMinutes;
  
  if (endMinutes >= 60) {
    endHours += Math.floor(endMinutes / 60);
    endMinutes %= 60;
  }
  
  // Format with leading zeros
  return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
};

// Calculate total pay based on schedule hours and hourly rate
const calculateTotalPay = (scheduleHour, hourlyRate) => {
  // Try to parse schedule hour and hourly rate
  let hours = 0;
  if (scheduleHour) {
    if (scheduleHour.includes(':')) {
      // Format HH:MM
      const [h, m] = scheduleHour.split(':').map(Number);
      hours = h + (m / 60);
    } else if (scheduleHour.includes('h') || scheduleHour.includes('m')) {
      // Format like "2h 30m"
      const hoursMatch = scheduleHour.match(/(\d+)h/);
      const minutesMatch = scheduleHour.match(/(\d+)m/);
      
      const h = hoursMatch ? parseInt(hoursMatch[1]) : 0;
      const m = minutesMatch ? parseInt(minutesMatch[1]) : 0;
      
      hours = h + (m / 60);
    } else {
      // Try to parse as a simple number
      hours = parseFloat(scheduleHour);
    }
  }
  
  const rate = parseFloat(hourlyRate || 0);
  
  if (isNaN(hours) || isNaN(rate)) {
    return 0;
  }
  
  return hours * rate;
};

const TutorClassLogPayment = () => {
  const { t } = useTranslation();
  const [classes, setClasses] = useState([]);
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [selectedMonthYear, setSelectedMonthYear] = useState(`${new Date().getFullYear()}-${new Date().getMonth() + 1}`);
  const [statusFilter, setStatusFilter] = useState('');
  const [, setLoading] = useState(false);
  const [registrationCounts, setRegistrationCounts] = useState({});
  const [adminapproveFilter, setAdminapproveFilter] = useState('');
  const [payoutFilter, setPayoutFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  const API_URL = process.env.REACT_APP_API_URL;
  
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No token found');
  
        // Fetch only: classes and registrations
        const [classRes, regRes] = await Promise.all([
          axios.get(`${API_URL}/classes/class-with-subject-group`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_URL}/registrations/registrations-count`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
  
        const classData = classRes.data;
  
        // Prepare registration count map
        const regCounts = {};
        if (Array.isArray(regRes.data)) {
          regRes.data.forEach(item => {
            regCounts[item.groupid] = item.registered_students;
          });
        }
        setRegistrationCounts(regCounts);
  
        // No need to merge with payment, just forward class data
        setClasses(classData);
        setFilteredClasses(classData);
  
      } catch (err) {
        console.error('❌ Fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
  
    fetchData();
  }, [API_URL]);
  
  const fetchHourlyRates = useCallback(async (classList) => {
    try {
      const token = localStorage.getItem('token');
  
      const enriched = await Promise.all(
        classList.map(async (cls) => {
          const { classid, groupid, schedule_hour } = cls;
          const studentCount = parseInt(registrationCounts[groupid] || '0', 10);
  
          try {
            const res = await axios.get(`${API_URL}/subject-group/hourly-rate`, {
              params: { groupId: groupid, scheduleHour: schedule_hour, studentCount },
              headers: { Authorization: `Bearer ${token}` },
            });
            
            // Calculate total pay
            const hourlyRate = res.data.hourlyRate;
            const totalPay = calculateTotalPay(schedule_hour, hourlyRate);
  
            return {
              ...cls,
              hourly_rate: hourlyRate,
              registered_students: res.data.studentCount,
              total_pay: totalPay
            };
          } catch (err) {
            console.error(`❌ Error for class ${classid}:`, err);
            return {
              ...cls,
              hourly_rate: 0,
              registered_students: studentCount,
              total_pay: 0
            };
          }
        })
      );
  
      setClasses(enriched); // ✅ Store enriched classes
    } catch (error) {
      console.error('❌ Error in fetchHourlyRates:', error);
    }
  }, [API_URL, registrationCounts]);
  
  

  useEffect(() => {
    if (classes.length > 0 && Object.keys(registrationCounts).length > 0) {
      fetchHourlyRates(classes);
    }
  }, [classes, registrationCounts, fetchHourlyRates]);
  
  
  useEffect(() => {
    const filtered = classes.filter(cls => {
      const classDate = new Date(cls.date);
      const classMonthYear = `${classDate.getFullYear()}-${classDate.getMonth() + 1}`;
      return (
        (selectedMonthYear ? classMonthYear === selectedMonthYear : true) &&
        (statusFilter ? cls.status === statusFilter : true) &&
        (adminapproveFilter ? cls.adminapprove === adminapproveFilter : true) &&
        (payoutFilter ? cls.payout === payoutFilter : true)
      );
    });
  
    setFilteredClasses(filtered);
    setCurrentPage(1);
  }, [selectedMonthYear, statusFilter, adminapproveFilter, payoutFilter, classes]);
  
  

  const csvData = filteredClasses.map(cls => ({
    No: cls.classid,
    Date: formatDate(cls.date),
    ClassCode: cls.class_code,
    ClassID: cls.classid,
    Subject: cls.subject_name,
    Teacher: cls.teacher_name,
    Time: cls.schedule_time && cls.schedule_hour ? 
      `${cls.schedule_time.split(':').slice(0, 2).join(':')} - ${calculateEndTime(cls.schedule_time.split(':').slice(0, 2).join(':'), cls.schedule_hour)}` : '',
    ScheduleHour: cls.schedule_hour,
    RegisteredStudents: cls.registered_students ?? 0,
    HourlyRate: cls.hourly_rate ? parseFloat(cls.hourly_rate).toFixed(2) : '0.00',
    TotalPay: cls.total_pay ? parseFloat(cls.total_pay).toFixed(2) : '0.00',
    Status: cls.status,
    AdminApprove: cls.adminapprove,
    Payout: cls.payout,
  }));
  
  // Calculate total sum for all filtered classes
  const totalSum = filteredClasses.reduce((sum, cls) => {
    return sum + (parseFloat(cls.total_pay || 0));
  }, 0);
 

  const columns = [
    { 
      title: t('date'), 
      dataIndex: 'date', 
      key: 'date',
      render: (date) => formatDate(date),
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
      ellipsis: true,
      width: 130,
      className: 'nowrap-cell',
    },
    { title: t('classcode'), dataIndex: 'class_code', key: 'class_code' },
    {
      title: t('Type'),
      dataIndex: 'isprivate',
      key: 'isprivate',
      align: 'center',
      render: (isprivate) => (
        <Tag color={isprivate ? 'purple' : 'green'}>
          {isprivate ? t('Private') : t('Group')}
        </Tag>
      ),
    },    
    { title: t('classid'), dataIndex: 'classid', key: 'classid' },
    { title: t('subject'), dataIndex: 'subject_name', key: 'subject_name' },
    { 
      title: t('teacher'), 
      dataIndex: 'teacher_name', 
      key: 'teacher_name',
      render: (_, cls) => `(${cls.nickname}) ${cls.teacher_name}`
    },
    { 
      title: t('time'), 
      dataIndex: 'schedule_time', 
      key: 'schedule_time',
      render: (time, record) => {
        if (!time || !record.schedule_hour) return '';
        
        // Remove seconds if present
        const startTime = time.split(':').slice(0, 2).join(':');
        const endTime = calculateEndTime(startTime, record.schedule_hour);
        
        return `${startTime} - ${endTime}`;
      }
    },
    { title: t('Hour'), dataIndex: 'schedule_hour', key: 'schedule_hour' },
    {
      title: t('Students'),
      dataIndex: 'registered_students',
      key: 'registered_students',
      align: 'center',
      render: (value) => value ?? '-',
    },
    {
      title: t('Hourly Rate'),
      dataIndex: 'hourly_rate',
      key: 'hourly_rate',
      align: 'right',
      render: (value) => (
        <span style={{ color: '#52c41a', fontWeight: 500 }}>
          {value ? parseFloat(value).toFixed(2) : '0.00'}
        </span>
      ),
    },
    {
      title: t('Total Pay'),
      dataIndex: 'total_pay',
      key: 'total_pay',
      align: 'right',
      render: (total_pay, record) => {
        // Use pre-calculated total or calculate it here
        let totalValue = total_pay;
        if (totalValue === undefined) {
          totalValue = calculateTotalPay(record.schedule_hour, record.hourly_rate);
        }
        
        return (
          <span style={{ color: '#f5222d', fontWeight: 'bold' }}>
            <DollarOutlined style={{ marginRight: 4 }} />
            {parseFloat(totalValue).toFixed(2)}
          </span>
        );
      },
      sorter: (a, b) => {
        const totalA = parseFloat(a.total_pay || 0);
        const totalB = parseFloat(b.total_pay || 0);
        return totalA - totalB;
      },
    },
    { 
      title: t('Tutor Confirm'), 
      dataIndex: 'status', 
      key: 'status',
      render: (status) => {
        const isApproved = status === 'approved';
        return (
          <Tag 
            color={isApproved ? '#f6ffed' : '#fffbe6'} 
            style={{ 
              color: isApproved ? '#52c41a' : '#faad14',
              borderColor: isApproved ? '#b7eb8f' : '#ffe58f'
            }}
          >
            {isApproved ? t('HOURSAPPROVED') : t('confirmpending')}
          </Tag>
        );
      }
    },
    { 
      title: t('Admin Confirm'), 
      dataIndex: 'adminapprove', 
      key: 'adminapprove',
      render: (adminapprove) => {
        const isConfirmed = adminapprove === 'adminconfirm';
        return (
          <Tag 
            color={isConfirmed ? '#f6ffed' : '#fffbe6'} 
            style={{ 
              color: isConfirmed ? '#52c41a' : '#faad14',
              borderColor: isConfirmed ? '#b7eb8f' : '#ffe58f'
            }}
          >
            {isConfirmed ? t('confirmed') : t('confirmpending')}
          </Tag>
        );
      }
    },
    { 
      title: t('payout'), 
      dataIndex: 'payout', 
      key: 'payout',
      render: (payout) => {
        const isPending = payout === 'pending';
        return (
          <Tag  
            color={isPending ? '#fff1f0' : '#f6ffed'} 
            style={{ 
              color: isPending ? '#f5222d' : '#52c41a',
              borderColor: isPending ? '#ffa39e' : '#b7eb8f'
            }}
          >
            {isPending ? t('Pending') : t('Paid')}
          </Tag>
        );
      }
    }
  ];

  return (
    <div>
        <Space style={{ marginBottom: 16 }}>
          <Select value={selectedMonthYear} onChange={setSelectedMonthYear} style={{ width: 160 }}>
            <Option value="">{t('All Months/Years')}</Option>
            {generateMonthYearOptions().map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>

          <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 160 }}>
            <Option value="">{t('classstatuses')}</Option>
            <Option value="approved">{t('confirmed')}</Option>
            <Option value="pending">{t('confirmpending')}</Option>
          </Select>

          <Select value={adminapproveFilter} onChange={setAdminapproveFilter} style={{ width: 160 }}>
            <Option value="">{t('Admin Approve')}</Option>
            <Option value="adminconfirm">{t('Admin Confirmed')}</Option> 
            <Option value="pending">{t('Pending')}</Option>
          </Select>

          <Select value={payoutFilter} onChange={setPayoutFilter} style={{ width: 160 }}>
            <Option value="">{t('payout')}</Option>
            <Option value="pending">{t('Pending')}</Option>
            <Option value="paid">{t('Paid')}</Option>
          </Select>

          <CSVLink data={csvData} filename={`tutor_class_log_${selectedMonthYear || 'all'}.csv`}>
            <Button icon={<FileExcelOutlined />} type="primary">{t('exportToCSV')}</Button>
          </CSVLink>
        </Space>

        <Table 
          columns={columns} 
          dataSource={filteredClasses} 
          rowKey="classid"
          pagination={{ 
            current: currentPage, 
            pageSize: itemsPerPage, 
            onChange: setCurrentPage 
          }}
          scroll={{ x: 'max-content' }}
          bordered
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={9} align="right">
                  <strong>{t('Total')}:</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={9}>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={10} align="right">
                  <strong style={{ color: '#f5222d' }}>
                    <DollarOutlined style={{ marginRight: 4 }} />
                    {totalSum.toFixed(2)}
                  </strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={11} colSpan={3}></Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
    </div>
  );
};

export default withRoleAccess(TutorClassLogPayment, ['superadmin']);